'use client';

import { KeyboardEventHandler, ReactNode, useMemo } from 'react';
import { CheckIcon } from 'lucide-react';

import { Text, useTranslations } from '@northladder/i18n';

import { cn } from '../../lib';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../command';
import { PopoverContent } from '../popover';
import { Separator } from '../separator';

import type {
  IMultiSelectGroup,
  IMultiSelectOption,
  IScreenSize,
} from './types';

interface ISelectPopOverContentProps {
  animDelay: number;
  animDuration: number;
  classPopoverAnimation: string;
  emptyIndicator: ReactNode;
  getAllOptions: () => IMultiSelectOption[];
  checkIfIsGroupedOptions: (
    opts: IMultiSelectOption[] | IMultiSelectGroup[]
  ) => opts is IMultiSelectGroup[];
  isSelectEnabled: boolean;
  listboxId: string;
  maxWidth: string;
  multiSelectId: string;
  onClearFilters: () => void;
  onClosePopover: () => void;
  onInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onSearchValueChange: (value: string) => void;
  onToggleAll: () => void;
  onToggleOption: (value: string) => void;
  options: IMultiSelectOption[] | IMultiSelectGroup[];
  popoverClassName?: string;
  screenSize: IScreenSize;
  searchable: boolean;
  searchValue: string;
  selectedValues: string[];
}

interface ISelectPopOverFooterProps {
  selectedItemsCount: number;
  onClosePopover: () => void;
  onClearFilters: () => void;
}

interface ISelectAllOptionProps {
  filterOptions: IMultiSelectOption[];
  selectedItemsCount: number;
  onToggleAll: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This displays trigger option that allows the user to select all options at once.
 */
function SelectAllOption({
  filterOptions,
  onToggleAll,
  selectedItemsCount,
}: ISelectAllOptionProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect');
  const count = filterOptions.length;

  return (
    <CommandGroup>
      <CommandItem
        key="all"
        aria-label={t('aria.selectAll', {
          total: count,
        })}
        aria-selected={
          selectedItemsCount ===
          filterOptions.filter((opt) => !opt.disabled).length
        }
        className="cursor-pointer"
        onSelect={onToggleAll}
        role="option"
      >
        <div
          aria-hidden="true"
          className={cn(
            `border-primary mr-2 flex h-4 w-4 items-center justify-center
                    rounded-sm border`,
            selectedItemsCount ===
              filterOptions.filter((opt) => !opt.disabled).length
              ? 'bg-primary text-primary-foreground'
              : 'opacity-50 [&_svg]:invisible'
          )}
        >
          <CheckIcon className="h-4 w-4" />
        </div>
        <span>
          {count > 20 ? t('selectAllWithCount', { count }) : t('selectAll')}
        </span>
      </CommandItem>
    </CommandGroup>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This displays the footer of the select popover.
 */
function SelectPopOverFooter({
  selectedItemsCount,
  onClosePopover,
  onClearFilters,
}: ISelectPopOverFooterProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect');

  return (
    <CommandGroup>
      <div className="flex items-center justify-between">
        {selectedItemsCount > 0 ? (
          <>
            <CommandItem
              className="flex-1 cursor-pointer justify-center"
              onSelect={onClearFilters}
            >
              {t('ctaClear')}
            </CommandItem>
            <Separator className="min-h-6 flex h-full" orientation="vertical" />
          </>
        ) : null}
        <CommandItem
          className="max-w-full flex-1 cursor-pointer justify-center"
          onSelect={onClosePopover}
        >
          {t('ctaClose')}
        </CommandItem>
      </div>
    </CommandGroup>
  );
}

interface IListsItemsContentProps
  extends Pick<
    ISelectPopOverContentProps,
    'checkIfIsGroupedOptions' | 'onToggleOption' | 'selectedValues'
  > {
  filteredOptions: IMultiSelectOption[] | IMultiSelectGroup[];
}

interface IListItemProps {
  selectedValues: string[];
  option: IMultiSelectOption;
  onToggleOption: (value: string) => void;
}

/**
 * -----------------------------------------------------------------------------
 * Renders a single list item which is part of a group of other selectable options.
 */
function GroupedListItem({
  selectedValues,
  option,
  onToggleOption,
}: IListItemProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect.aria');
  const {
    disabled: isDisabled,
    icon: Icon,
    label,
    labelStr,
    trailingText,
    value,
  } = option;
  const isSelected = selectedValues.includes(value);
  let ariaLabel = labelStr;

  if (isSelected) {
    if (isDisabled) {
      ariaLabel = t('optionItemSelectedDisabled', { labelStr });
    } else {
      ariaLabel = t('optionItemSelected', { labelStr });
    }
  } else if (isDisabled) {
    ariaLabel = t('optionItemDisabled', { labelStr });
  } else {
    ariaLabel = t('optionItemNotSelected', { labelStr });
  }

  const handleOnSelect = () => {
    onToggleOption(value);
  };

  return (
    <CommandItem
      key={value}
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      aria-selected={isSelected}
      className={cn(
        'cursor-pointer',
        isDisabled && 'cursor-not-allowed opacity-50'
      )}
      disabled={isDisabled}
      onSelect={handleOnSelect}
      role="option"
    >
      <div
        aria-hidden="true"
        className={cn(
          `border-primary mr-2 flex h-4 w-4 items-center justify-center
          rounded-sm border`,
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'opacity-50 [&_svg]:invisible'
        )}
      >
        <CheckIcon className="h-4 w-4" />
      </div>

      {Icon ? (
        <Icon
          aria-hidden="true"
          className="text-muted-foreground mr-2 h-4 w-4"
        />
      ) : null}

      <div className="flex flex-1 items-center justify-between">
        <Text text={label} />

        {trailingText ? (
          <span className="text-muted-foreground ml-2 text-xs">
            {trailingText}
          </span>
        ) : null}
      </div>
    </CommandItem>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Renders a single list item which is isolated from other selectable options.
 */
function StandaloneListItem({
  selectedValues,
  option,
  onToggleOption,
}: IListItemProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect.aria');

  const {
    disabled: isDisabled,
    icon: Icon,
    label,
    labelStr,
    trailingText,
    value,
  } = option;
  const isSelected = selectedValues.includes(value);
  let ariaLabel = labelStr;

  if (isSelected) {
    if (isDisabled) {
      ariaLabel = t('optionItemSelectedDisabled', { labelStr });
    } else {
      ariaLabel = t('optionItemSelected', { labelStr });
    }
  } else if (isDisabled) {
    ariaLabel = t('optionItemDisabled', { labelStr });
  } else {
    ariaLabel = t('optionItemNotSelected', { labelStr });
  }

  const handleOnSelect = () => {
    onToggleOption(value);
  };

  return (
    <CommandItem
      key={value}
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      aria-selected={isSelected}
      className={cn(
        'cursor-pointer',
        isDisabled && 'cursor-not-allowed opacity-50'
      )}
      disabled={isDisabled}
      onSelect={handleOnSelect}
      role="option"
    >
      <div
        aria-hidden="true"
        className={cn(
          `border-primary mr-2 flex h-4 w-4 items-center justify-center
          rounded-sm border`,
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'opacity-50 [&_svg]:invisible'
        )}
      >
        <CheckIcon className="h-4 w-4" />
      </div>

      {Icon ? (
        <Icon
          aria-hidden="true"
          className="text-muted-foreground mr-2 h-4 w-4"
        />
      ) : null}

      <div className="flex flex-1 items-center justify-between">
        <Text text={label} />
        {trailingText ? (
          <span className="text-muted-foreground ml-2 text-xs">
            {trailingText}
          </span>
        ) : null}
      </div>
    </CommandItem>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Renders the list of the items in the multi select popover component.
 */
function ListsItemsContent({
  filteredOptions,
  checkIfIsGroupedOptions,
  selectedValues,
  onToggleOption,
}: IListsItemsContentProps) {
  const isGrouped = checkIfIsGroupedOptions(filteredOptions);

  if (isGrouped) {
    return filteredOptions.map((group) => (
      <CommandGroup key={group.heading} heading={group.heading}>
        {group.options.map((option) => (
          <GroupedListItem
            key={option.value}
            onToggleOption={onToggleOption}
            option={option}
            selectedValues={selectedValues}
          />
        ))}
      </CommandGroup>
    ));
  }

  return (
    <CommandGroup>
      {filteredOptions.map((option) => (
        <StandaloneListItem
          key={option.value}
          onToggleOption={onToggleOption}
          option={option}
          selectedValues={selectedValues}
        />
      ))}
    </CommandGroup>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This displays the content of the select popover once clicked.
 */
export function SelectPopOverContent({
  animDelay,
  animDuration,
  checkIfIsGroupedOptions,
  classPopoverAnimation,
  emptyIndicator,
  getAllOptions,
  isSelectEnabled,
  listboxId,
  maxWidth,
  multiSelectId,
  onClearFilters,
  onClosePopover,
  onInputKeyDown,
  onSearchValueChange,
  onToggleAll,
  onToggleOption,
  options,
  popoverClassName,
  screenSize,
  searchable,
  searchValue,
  selectedValues,
}: ISelectPopOverContentProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect');

  const selectedItemsCount = selectedValues.length;
  const filterOptions = getAllOptions();

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue) return options;

    if (options.length === 0) return [];

    if (checkIfIsGroupedOptions(options)) {
      return options
        .map((group) => ({
          ...group,
          options: group.options.filter(
            ({ labelStr, value }) =>
              labelStr.toLowerCase().includes(searchValue.toLowerCase()) ||
              value.toLowerCase().includes(searchValue.toLowerCase())
          ),
        }))
        .filter((group) => group.options.length > 0);
    }

    return options.filter(
      ({ labelStr, value }) =>
        labelStr.toLowerCase().includes(searchValue.toLowerCase()) ||
        value.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue, searchable, checkIfIsGroupedOptions]);

  return (
    <PopoverContent
      align="start"
      aria-label={t('availableOptions')}
      aria-multiselectable="true"
      className={cn(
        'w-auto p-0',
        classPopoverAnimation,
        screenSize === 'mobile' && 'w-[85vw] max-w-[280px]',
        screenSize === 'tablet' && 'w-[70vw] max-w-md',
        screenSize === 'desktop' && 'min-w-[300px]',
        popoverClassName
      )}
      id={listboxId}
      onEscapeKeyDown={onClosePopover}
      role="listbox"
      style={{
        animationDuration: `${animDuration}s`,
        animationDelay: `${animDelay}s`,
        maxWidth: `min(${maxWidth}, 85vw)`,
        maxHeight: screenSize === 'mobile' ? '70vh' : '60vh',
        touchAction: 'manipulation',
      }}
    >
      <Command>
        {searchable ? (
          <>
            <CommandInput
              aria-describedby={`${multiSelectId}-search-help`}
              aria-label={t('searchLabel')}
              onKeyDown={onInputKeyDown}
              onValueChange={onSearchValueChange}
              placeholder={t('searchPlaceholder')}
              value={searchValue}
            />

            <div className="sr-only" id={`${multiSelectId}-search-help`}>
              {t('aria.screenReaderFilter')}
            </div>
          </>
        ) : null}

        <CommandList
          className={cn(
            'multiselect-scrollbar max-h-[40vh] overflow-y-auto',
            screenSize === 'mobile' && 'max-h-[50vh]',
            'overscroll-behavior-y-contain'
          )}
        >
          <CommandEmpty>{emptyIndicator || t('noResultsFound')}</CommandEmpty>

          {isSelectEnabled && !searchValue ? (
            <SelectAllOption
              filterOptions={filterOptions}
              onToggleAll={onToggleAll}
              selectedItemsCount={selectedItemsCount}
            />
          ) : null}

          <ListsItemsContent
            checkIfIsGroupedOptions={checkIfIsGroupedOptions}
            filteredOptions={filteredOptions}
            onToggleOption={onToggleOption}
            selectedValues={selectedValues}
          />

          <CommandSeparator />

          <SelectPopOverFooter
            onClearFilters={onClearFilters}
            onClosePopover={onClosePopover}
            selectedItemsCount={selectedItemsCount}
          />
        </CommandList>
      </Command>
    </PopoverContent>
  );
}
